import type {
	Collection,
	CollectionDefaults,
	FindOptions,
	ResolvedCollection,
	StoreCore,
	StoreSchema,
} from '@rstore/shared';
import type { CrudActionType as CrudActionName } from '#layers/access/public';
import type { FieldSelection, Filter, PrimitivePrimaryKey, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { isNil, stringifyQuery } from '#layers/base/public';
import { isNonEmptyFilter, isRelationalFilter, mergeAnd, resolveFieldSelectionDefaults, toApplicableFilter } from '#layers/data-model/public';
import { apiEndpoint } from './api-endpoint';
import { getSelection } from './get-selection';
import {
	hasIncludeFilter,
	normalizeFilterOperandsForBackend,
	normalizeIncludeFilterOperandsForBackend,
} from './normalize-filter-operands-for-backend';
import { resolveSchemaCollection } from './resolve-schema-collection';

interface GetFetchOptions {
	store: StoreCore<StoreSchema, CollectionDefaults>;
	collection: ResolvedCollection;
	findOptions?: FindOptions<Collection, CollectionDefaults, StoreSchema>;
	key?: PrimitivePrimaryKey;
	selection?: FieldSelection;
	filter?: Filter;
	operation: CrudActionName;
}

/**
 * Combines endpoint, fields, filters, pagination, and custom params into the API request shape.
 *
 * Structured filters are normalized before query-string serialization so canonical in-memory
 * operands, such as Temporal date/time values, become backend wire values at one boundary.
 */
export function getFetchOptions(options: GetFetchOptions) {
	const { store, collection, findOptions, key, filter, operation } = options;

	const endpoint = apiEndpoint({ collection, key, operation });
	const rawSelection = resolveFieldSelectionDefaults(options.selection ?? getSelection({ store, collection, findOptions }));

	let finalFilter: Filter | undefined;

	if (isNonEmptyFilter(filter) && isNonEmptyFilter(findOptions?.filter)) {
		finalFilter = mergeAnd(filter, findOptions.filter);
	}
	else if (isNonEmptyFilter(filter)) {
		finalFilter = filter;
	}
	else if (isNonEmptyFilter(findOptions?.filter)) {
		finalFilter = findOptions.filter;
	}

	// Normalize open ranges and remove incomplete clauses before request serialization.
	finalFilter = finalFilter ? toApplicableFilter(finalFilter) : finalFilter;

	const include = rawSelection.include;
	const hasNestedFilter = include ? hasIncludeFilter(include) : false;
	const needsSchemaCollection = hasNestedFilter || isRelationalFilter(finalFilter);
	const schemaCollection = needsSchemaCollection
		? resolveSchemaCollection({ collectionName: collection.name, store })
		: undefined;
	const primitiveFieldTypes = collection.meta?.primitiveFieldTypes;
	const normalizationContext = {
		primitiveFieldTypes,
		schemaCollection,
		getPrimitiveFieldTypes: (schemaCollection: ResolvedMonospaceCollection) => {
			const collection = store.$collections.find((collection) => collection.name === schemaCollection.apiName);

			return collection?.meta?.primitiveFieldTypes;
		},
	};

	const normalizedFilter = finalFilter
		? normalizeFilterOperandsForBackend(finalFilter, normalizationContext)
		: finalFilter;
	const normalizedInclude = include && hasNestedFilter && normalizationContext.schemaCollection
		? normalizeIncludeFilterOperandsForBackend(include, normalizationContext)
		: include;

	const { pageSize, pageIndex } = findOptions ?? {};
	const hasPage = !isNil(pageIndex);
	const hasPageSize = !isNil(pageSize);
	const paginated = hasPage && hasPageSize;

	if (hasPage && !hasPageSize) {
		console.error('findOptions has pageIndex but no pageSize', findOptions);
	}

	const query = stringifyQuery({
		fields: rawSelection.fields,
		filter: normalizedFilter,
		sort: findOptions?.sort,
		limit: findOptions?.limit,
		offset: findOptions?.offset,
		include: normalizedInclude,
		...findOptions?.params,
		...paginated
			? {
				// Simple pagination = previous/next
				// Load an extra item to check for next page
				// TODO handle full pagination (shouldn't need to add an extra item)
					meta: { totalCount: true },
					limit: pageSize + 1,
					offset: pageIndex * pageSize,
				}
			: {},
	});

	// Sanity checks
	if (findOptions?.fetchPolicy !== 'no-cache' && isRelationalFilter(filter)) {
		console.warn(`The following query contains a relational filter that can not be safely evaluated on the client: ${endpoint}?${query}`, filter);
	}

	return {
		endpoint,
		query,
		paginated,
	};
}
